/**
 * Pricing utility for ASMM billing cycle calculations.
 * Yearly billing = monthly × 12 × 0.90 (10% discount).
 */

export const YEARLY_DISCOUNT_PERCENT = 10;
export const YEARLY_MONTHS = 12;

/**
 * Calculate the yearly price for a given monthly rate.
 * Formula: monthlyPrice × 12 × 0.90 (rounded to nearest integer)
 */
export function getYearlyPrice(monthlyPrice: number, overrideYearlyPrice?: number): number {
  if (overrideYearlyPrice && overrideYearlyPrice > 0) return overrideYearlyPrice;
  return Math.round(monthlyPrice * YEARLY_MONTHS * (1 - YEARLY_DISCOUNT_PERCENT / 100));
}

/**
 * Calculate the savings when paying yearly vs 12 individual monthly payments.
 */
export function getYearlySavings(monthlyPrice: number, overrideYearlyPrice?: number): number {
  return (monthlyPrice * YEARLY_MONTHS) - getYearlyPrice(monthlyPrice, overrideYearlyPrice);
}

/**
 * Get the billing amount based on the selected cycle.
 */
export function getBillingAmount(
  monthlyPrice: number,
  cycle: 'Bulanan' | 'Tahunan',
  overrideYearlyPrice?: number
): number {
  return cycle === 'Tahunan' ? getYearlyPrice(monthlyPrice, overrideYearlyPrice) : monthlyPrice;
}

/**
 * Check if a fee category supports billing cycle selection.
 * Only recurring monthly fees and special packages support yearly billing.
 * One-time fees (Pendaftaran, Bengkung, Sumbangan) do NOT support billing cycles.
 */
export function supportsBillingCycle(category: string): boolean {
  return category === 'Yuran Bulanan' || category === 'Pakej Khas';
}

/**
 * Check if a member is eligible for monthly fee billing (i.e. is a regular student/murid).
 * Excludes management, coaches (jurulatih), and roles with 'pelepasan_yuran_bulanan'.
 */
export function isEligibleForMonthlyFee(
  member: { role?: string; roles?: string[]; bengkung?: string; email?: string; id?: string; icNo?: string; isAlsoPesilat?: boolean; onboardingStatus?: string; status?: string },
  rolePermsMap?: Record<string, string[]>
): boolean {
  if (!member) return false;

  // 1. Incomplete profile check: member must have completed profile with IC number
  if (!member.icNo || member.icNo.trim() === '') return false;

  // 2. New members pending onboarding are NOT charged separate monthly fee
  // (Their onboarding package already includes uniform, annual membership, and the first month's training fee)
  if ((member as any).onboardingStatus === 'pending_payment' || (member as any).status === 'Menunggu Pengesahan') {
    return false;
  }

  // 3. Waris (Parent / Guardian) is NOT a pesilat and is NEVER charged monthly silat training fee
  // (Only charge if explicitly participating as a pesilat with isAlsoPesilat === true)
  if (member.role === 'Waris' && !(member as any).isAlsoPesilat) {
    return false;
  }

  const memberRoles: string[] = (member as any).roles && Array.isArray((member as any).roles) && (member as any).roles.length > 0
    ? (member as any).roles
    : (member.role ? [member.role] : ['Ahli']);

  if (memberRoles.includes('Waris') && !(member as any).isAlsoPesilat) {
    return false;
  }

  // 4. Superadmin / Founder exemption
  const email = (member.email || '').toLowerCase().trim();
  const id = (member.id || '').toLowerCase().trim();
  if (
    email === 'meisterhafiz@gmail.com' ||
    email === 'hafiz@asmm.org.my' ||
    id === 'asmm-011' ||
    member.role === 'Superadmin' ||
    memberRoles.includes('Superadmin')
  ) {
    return false;
  }

  // 5. Explicit role permission check for 'pelepasan_yuran_bulanan'
  if (rolePermsMap && memberRoles.some(r => rolePermsMap[r]?.includes('pelepasan_yuran_bulanan'))) {
    return false;
  }

  // 6. Known management and leadership roles exempt by default
  const exemptRoles = [
    'pengerusi',
    'timbalan pengerusi',
    'guru utama',
    'jurulatih kanan',
    'jurulatih',
    'jurulatih muda',
    'ketua gelanggang',
    'bendahari',
    'bendahari gelanggang',
    'setiausaha',
    'pen. setiausaha',
    'penolong setiausaha',
    'ajk',
    'ahli jawatankuasa',
    'pemeriksa kira-kira',
    'waris'
  ];
  const hasExemptRole = memberRoles.some(r => {
    const roleLower = (r || '').toLowerCase().trim();
    return exemptRoles.includes(roleLower) || roleLower.startsWith('ketua biro') || roleLower.includes('biro');
  });
  if (hasExemptRole) {
    return false;
  }

  // 7. Only pure 'Ahli' (regular practicing student) is charged
  if (memberRoles.length > 0 && !memberRoles.includes('Ahli')) return false;

  // 8. Coaches (jurulatih) with Merah (Pembantu Jurulatih) or Kuning (Jurulatih) or Guru Utama bengkung ranks are not charged
  const bengkung = (member.bengkung || '').toLowerCase();
  const isCoach = bengkung.includes('merah') ||
    bengkung.includes('kuning') ||
    bengkung.includes('guru') ||
    bengkung.includes('jurulatih');

  if (isCoach) return false;

  return true;
}

/**
 * Returns the reason/label why a member receives monthly fee exemption (or null if not exempt).
 */
export function getMonthlyFeeExemptionReason(
  member: { role?: string; roles?: string[]; bengkung?: string; email?: string; id?: string },
  rolePermsMap?: Record<string, string[]>
): string | null {
  if (isEligibleForMonthlyFee(member, rolePermsMap)) return null;

  const email = (member.email || '').toLowerCase().trim();
  const id = (member.id || '').toLowerCase().trim();
  const memberRoles: string[] = member.roles && Array.isArray(member.roles) && member.roles.length > 0
    ? member.roles
    : (member.role ? [member.role] : ['Ahli']);

  if (email === 'meisterhafiz@gmail.com' || email === 'hafiz@asmm.org.my' || id === 'asmm-011' || memberRoles.includes('Superadmin')) {
    return 'Pelepasan Pentadbir';
  }

  if (memberRoles.includes('Waris') && memberRoles.length === 1) {
    return 'Waris / Penjaga (Bukan Pesilat)';
  }

  const mgmtRole = memberRoles.find(r => ['Pengerusi', 'Timbalan Pengerusi', 'Bendahari', 'Bendahari Gelanggang', 'Setiausaha', 'Pen. Setiausaha', 'Ketua Gelanggang', 'AJK'].includes(r));
  if (mgmtRole) {
    return `Pelepasan Pengurusan (${mgmtRole})`;
  }

  const bengkung = (member.bengkung || '').toLowerCase();
  if (bengkung.includes('merah') || bengkung.includes('kuning') || bengkung.includes('guru') || bengkung.includes('jurulatih') || memberRoles.some(r => (r || '').toLowerCase().includes('jurulatih'))) {
    return 'Pelepasan Tenaga Jurulatih';
  }

  return 'Pengecualian Yuran Bulanan';
}

/**
 * Check if a member is eligible for yearly fee billing.
 * Excludes roles with 'pelepasan_yuran_tahunan' permission.
 */
export function isEligibleForYearlyFee(
  member: { role?: string; roles?: string[]; bengkung?: string },
  rolePermsMap?: Record<string, string[]>
): boolean {
  if (!member) return false;

  const memberRoles: string[] = member.roles && Array.isArray(member.roles) && member.roles.length > 0
    ? member.roles
    : (member.role ? [member.role] : ['Ahli']);

  if (rolePermsMap && memberRoles.some(r => rolePermsMap[r]?.includes('pelepasan_yuran_tahunan'))) {
    return false;
  }

  return true;
}

import { isUserAbove18, isSuperAdmin, calculateAge } from './formatters';
import { GelanggangClassItem, UserProfile, InvoiceItem, InvoiceLineItem, MerchandiseItem } from '../types';

/**
 * Parse offered classes from Gelanggang classes JSON string with default fallbacks.
 */
export function parseGelanggangClasses(
  gName: string,
  classesStr?: string
): Array<GelanggangClassItem> {
  if (classesStr) {
    try {
      const parsed = JSON.parse(classesStr);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((c: any) => ({
          name: String(c.name || 'Silat'),
          price: Number(c.price) >= 0 ? Number(c.price) : 40,
          schedule: c.schedule || undefined,
          day: c.day || undefined,
          startTime: c.startTime || undefined,
          endTime: c.endTime || undefined,
          category: c.category || (String(c.name || '').toLowerCase().includes('dewasa') ? 'Dewasa' : String(c.name || '').toLowerCase().includes('muda') ? 'Kanak-kanak' : 'Terbuka'),
          notes: c.notes || undefined
        }));
      }
    } catch { }
  }
  if (gName === 'Pusaka Warisan') {
    return [
      { name: 'Latihan Silat (Muda & Dewasa)', price: 20, category: 'Terbuka', day: 'Jumaat', startTime: '08:00', endTime: '11:00', schedule: 'Jumaat (8.00 AM - 11.00 AM)', notes: 'Sesi pagi bersama pesilat muda dan dewasa' },
      { name: 'Latihan Silat (Dewasa)', price: 30, category: 'Dewasa', day: 'Sabtu', startTime: '20:00', endTime: '22:00', schedule: 'Sabtu (8.00 PM - 10.00 PM)', notes: 'Sesi malam kependekaran pesilat dewasa' }
    ];
  }
  if (gName === 'Wadi AnNour') {
    return [
      { name: 'Latihan Memanah', price: 20, category: 'Terbuka', day: 'Jumaat', startTime: '15:30', endTime: '17:00', schedule: 'Jumaat (3.30 PM - 5.00 PM)', notes: 'Sesi memanah tradisional petang Jumaat' },
      { name: 'Latihan Silat', price: 20, category: 'Terbuka', day: 'Jumaat', startTime: '17:00', endTime: '18:30', schedule: 'Jumaat (5.00 PM - 6.30 PM)', notes: 'Sesi silat tempur dan seni petang Jumaat' }
    ];
  }
  return [{ name: 'Silat', price: 40, category: 'Terbuka' }];
}

/**
 * Look up the exact price of a plan for a given gelanggang, dynamically respecting
 * the classes and fee amounts configured in the Gelanggang database.
 */
export function getGelanggangClassPrice(
  gelName: string,
  planName: string,
  gelanggangList?: Array<{ name: string; classes?: string }>,
  isAdult?: boolean
): number {
  if (gelanggangList && gelanggangList.length > 0) {
    const gel = gelanggangList.find(g => g.name === gelName);
    if (gel) {
      const classes = parseGelanggangClasses(gelName, gel.classes);
      // Exact match
      const exact = classes.find(c => c.name.trim().toLowerCase() === planName.trim().toLowerCase());
      if (exact) return exact.price;

      // Silat match if requested planName is generic 'Silat'
      if (planName.trim().toLowerCase() === 'silat') {
        if (isAdult !== undefined) {
          if (isAdult) {
            const adult = classes.find(c => c.name.toLowerCase().includes('dewasa') || c.category === 'Dewasa');
            if (adult) return adult.price;
          } else {
            const youth = classes.find(c =>
              c.name.toLowerCase().includes('muda') ||
              c.name.toLowerCase().includes('kanak') ||
              c.category === 'Kanak-kanak'
            );
            if (youth) return youth.price;
          }
        }
        const anySilat = classes.find(c => c.name.toLowerCase().includes('silat'));
        if (anySilat) return anySilat.price;
        if (classes.length > 0) return classes[0].price;
      }
    }
  }

  // Sensible default fallbacks if not configured in DB
  if (planName === 'Memanah') return 50;
  if (planName === 'Silat Dewasa') return gelName === 'Wadi AnNour' ? 40 : 50;
  if (planName === 'Silat Muda' || planName === 'Silat Kanak-kanak') return gelName === 'Wadi AnNour' ? 20 : 30;
  if (gelName === 'Wadi AnNour') return isAdult === false ? 20 : 40;
  return 40;
}

/**
 * Resolves a member's active enrollments.
 * If the member has a JSON enrollment list saved in sessionTime, parses it.
 * If sessionTime is a legacy schedule string or empty, smartly resolves the default plan
 * based on the member's assigned gelanggang's actual configured classes (matching youth/adult appropriately).
 */
export function resolveMemberEnrollments(
  member: { gelanggang?: string; sessionTime?: string; icNo?: string; relationship?: string; role?: string },
  gelanggangList?: Array<{ name: string; classes?: string }>
): Array<{ gelanggang: string; plans: string[] }> {
  if (member.sessionTime) {
    try {
      const parsed = JSON.parse(member.sessionTime);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    } catch { }

    if (typeof member.sessionTime === 'string' && member.gelanggang && member.gelanggang !== 'Tiada') {
      const gel = gelanggangList?.find(g => g.name === member.gelanggang);
      const classes = parseGelanggangClasses(member.gelanggang, gel?.classes);
      const sLower = member.sessionTime.toLowerCase();
      const matchedClass = classes.find(c => sLower.includes(c.name.toLowerCase()) || c.name.toLowerCase().includes(sLower));
      if (matchedClass) {
        return [{ gelanggang: member.gelanggang, plans: [matchedClass.name] }];
      }
    }
  }

  // Fallback for members without JSON enrollment
  if (member.gelanggang && member.gelanggang !== 'Tiada' && member.gelanggang !== 'Semua Gelanggang') {
    const gel = gelanggangList?.find(g => g.name === member.gelanggang);
    const classes = parseGelanggangClasses(member.gelanggang, gel?.classes);

    // Check if member is youth (under 18 or relationship is Anak)
    const isAdult = member.icNo ? isUserAbove18(member.icNo) : (member.relationship !== 'Anak');

    if (isAdult) {
      // Look for dedicated adult silat class if present
      const adultClass = classes.find(c => c.name.toLowerCase().includes('dewasa') && !c.name.toLowerCase().includes('muda'))
        || classes.find(c => c.name.toLowerCase().includes('dewasa'));
      if (adultClass) {
        return [{ gelanggang: member.gelanggang, plans: [adultClass.name] }];
      }
    } else {
      // Look for youth/muda silat class if present
      const youthClass = classes.find(c =>
        c.name.toLowerCase().includes('muda') ||
        c.name.toLowerCase().includes('kanak') ||
        c.name.toLowerCase().includes('sekolah')
      );
      if (youthClass) {
        return [{ gelanggang: member.gelanggang, plans: [youthClass.name] }];
      }
    }

    // Default to the first silat class or first offered class
    const silatClass = classes.find(c => c.name.toLowerCase().includes('silat')) || classes[0];
    const defaultPlan = silatClass ? silatClass.name : 'Silat';

    return [{ gelanggang: member.gelanggang, plans: [defaultPlan] }];
  }

  return [];
}

/**
 * Official ASMM Uniform Sizes (Height in cm, increment of 10cm from 110cm to 200cm)
 */
export const UNIFORM_SIZES = [
  '110cm',
  '120cm',
  '130cm',
  '140cm',
  '150cm',
  '160cm',
  '170cm',
  '180cm',
  '190cm',
  '200cm',
  '210cm'
] as const;

export type UniformSize = typeof UNIFORM_SIZES[number];

/**
 * Calculates uniform price based on height/size range:
 * - 110cm to 130cm: RM 120
 * - 140cm to 170cm: RM 130
 * - 180cm to 210cm: RM 140
 */
export function getUniformPriceBySize(size?: string | null): number {
  if (!size) return 130;
  const num = parseInt(size.replace(/[^0-9]/g, ''), 10);
  if (isNaN(num)) return 130;
  if (num >= 110 && num <= 130) return 120;
  if (num >= 140 && num <= 170) return 130;
  if (num >= 180) return 140;
  if (num < 110) return 120;
  return 140;
}

/**
 * Returns the price for a merchandise item given an optional selected size.
 * Prioritizes item.sizePrices[size], then uniform sizing logic for Baju Silat,
 * and finally item.price.
 */
export function getProductPriceBySize(item?: MerchandiseItem | null, size?: string | null): number {
  if (!item) return 0;
  // 1. If item has sizePrices and this specific size has a price set
  if (size && item.sizePrices && item.sizePrices[size] !== undefined && Number(item.sizePrices[size]) > 0) {
    return Number(item.sizePrices[size]);
  }
  // 2. If item has sizePrices map configured, but this specific size wasn't found or size is null
  if (item.sizePrices && Object.keys(item.sizePrices).length > 0) {
    const valid = Object.values(item.sizePrices).filter(p => typeof p === 'number' && !isNaN(p) && p > 0);
    if (valid.length > 0) {
      return (item.price && item.price > 0) ? item.price : valid[0];
    }
  }
  // 3. If item has custom price explicitly set and different from default seed (120)
  if (item.price && Number(item.price) > 0 && item.price !== 120) {
    return item.price;
  }
  // 4. Default Baju Silat formula if no custom pricing or sizePrices was ever configured
  if (item.id === 'merch-baju-silat' || item.name.toLowerCase().includes('baju silat')) {
    return getUniformPriceBySize(size || '150cm');
  }
  return item.price || 0;
}

/**
 * Returns the min/max price range info and display label for a product.
 */
export function getProductPriceRange(item: MerchandiseItem): { min: number; max: number; hasRange: boolean; label: string } {
  if (item.sizePrices && Object.keys(item.sizePrices).length > 0) {
    const validPrices = Object.values(item.sizePrices).filter(p => typeof p === 'number' && !isNaN(p) && p > 0);
    if (validPrices.length > 0) {
      const min = Math.min(...validPrices);
      const max = Math.max(...validPrices);
      return {
        min,
        max,
        hasRange: min !== max,
        label: min === max ? `RM ${min.toFixed(2)}` : `RM ${min.toFixed(2)} – RM ${max.toFixed(2)}`
      };
    }
  }
  // If item has a custom price set and different from default 120
  if (item.price && Number(item.price) > 0 && item.price !== 120) {
    return { min: item.price, max: item.price, hasRange: false, label: `RM ${item.price.toFixed(2)}` };
  }
  if (item.id === 'merch-baju-silat' || item.name.toLowerCase().includes('baju silat')) {
    return { min: 120, max: 140, hasRange: true, label: 'RM 120.00 – RM 140.00' };
  }
  return { min: item.price || 0, max: item.price || 0, hasRange: false, label: `RM ${(item.price || 0).toFixed(2)}` };
}

/**
 * Calculates onboarding fees for a new Ahli:
 * - Uniform: RM 120 - RM 140 (based on chosen size)
 * - Yearly Fee: RM 10 for now (<2027), RM 20 starting 2027
 * - Monthly Fee: RM 30 for adults (>=18 yrs), RM 20 for below 18 yrs, based on IC number
 */
export function calculateOnboardingFees(
  icNo?: string | null,
  customYear?: number,
  uniformSize?: string | null
) {
  const year = customYear || new Date().getFullYear();
  const selectedSize = uniformSize || '150cm';
  const uniformFee = getUniformPriceBySize(selectedSize);
  const yearlyFee = year >= 2027 ? 20 : 10;
  const isAdult = isUserAbove18(icNo || '', year);
  const age = calculateAge(icNo || '', year);
  const monthlyFee = isAdult ? 30 : 20;
  const total = uniformFee + yearlyFee + monthlyFee;
  const ageLabel = isAdult ? 'Dewasa (18 Tahun ke Atas)' : 'Kanak-kanak / Remaja (Bawah 18 Tahun)';

  return {
    uniformFee,
    uniformSize: selectedSize,
    yearlyFee,
    monthlyFee,
    isAdult,
    age,
    ageLabel,
    total,
    year
  };
}

/**
 * Creates an itemized onboarding invoice for a new Ahli with uniform size itemization.
 */
export function createOnboardingInvoice(member: UserProfile, chosenSize?: string): InvoiceItem {
  const size = chosenSize || member.uniformSize || '150cm';
  const fees = calculateOnboardingFees(member.icNo, undefined, size);
  const now = new Date();
  const randSuffix = Math.floor(1000 + Math.random() * 9000);
  const invoiceNo = `INV-ONB-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${(member.id || 'NEW').replace(/[^a-zA-Z0-9]/g, '').slice(-6)}-${randSuffix}`;

  const items: InvoiceLineItem[] = [
    {
      id: 'item-uniform',
      description: `Pakej Uniform Rasmi Lengkap ASMM (Saiz: ${size} - Baju, Seluar & Bengkung Putih Asas)`,
      quantity: 1,
      unitPrice: fees.uniformFee,
      amount: fees.uniformFee
    },
    {
      id: 'item-yearly',
      description: `Yuran Pendaftaran & Keahlian Tahunan ASMM (${fees.year})`,
      quantity: 1,
      unitPrice: fees.yearlyFee,
      amount: fees.yearlyFee
    },
    {
      id: 'item-monthly',
      description: `Yuran Bulanan Sesi Latihan Pertama (${fees.ageLabel})`,
      quantity: 1,
      unitPrice: fees.monthlyFee,
      amount: fees.monthlyFee
    }
  ];

  return {
    id: `inv-onb-${member.id || Date.now()}-${Date.now()}`,
    invoiceNo,
    memberId: member.id,
    memberName: member.name,
    memberIcNo: member.icNo,
    memberPhone: member.phone,
    address: member.address,
    payerType: 'Ahli',
    gelanggang: member.gelanggang || 'Pusaka Warisan',
    category: 'Yuran Pendaftaran',
    title: `Pakej Pendaftaran Ahli Baharu ASMM (${member.name})`,
    description: `Pakej kemasukan pesilat baharu: Uniform rasmi saiz ${size} (RM${fees.uniformFee}), Yuran tahunan ${fees.year} (RM${fees.yearlyFee}), dan Yuran latihan bulanan pertama (RM${fees.monthlyFee}).`,
    items,
    amount: fees.total,
    dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    createdAt: now.toISOString(),
    status: 'Belum Dibayar'
  };
}

/**
 * Determines whether a user is a new Ahli who must settle their onboarding payment
 * before being allowed to access the dashboard.
 */
export function isNewAhliPendingOnboarding(user?: UserProfile | null, invoices?: InvoiceItem[]): boolean {
  if (!user) return false;

  // Superadmins and higher committee members are exempt
  if (isSuperAdmin(user) || ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Penolong Setiausaha', 'Bendahari', 'AJK', 'Ketua Gelanggang', 'Pemeriksa Kira-Kira', 'Jurulatih'].includes(user.role)) {
    return false;
  }

  // Pure Waris accounts (guardians who do not practice silat themselves) are exempt
  if (user.role === 'Waris' || (user.roles && user.roles.includes('Waris') && !(user as any).isAlsoPesilat)) {
    return false;
  }

  // Explicitly marked as old member or completed onboarding
  if (user.isOldMember === true || user.onboardingStatus === 'completed' || user.onboardingStatus === 'exempted') {
    return false;
  }

  // Pre-system seed members (ASMM-001 to ASMM-020) are old members
  if (user.id && /^ASMM-0[0-1][0-9]$/i.test(user.id)) {
    return false;
  }

  // If rejected or cuti, not pending onboarding
  if (user.status === 'Ditolak' || user.status === 'Cuti') {
    return false;
  }

  // If invoice already marked as 'Lulus / Dibayar'
  if (invoices && invoices.length > 0) {
    const onbInv = invoices.find(i =>
      (i.memberId === user.id || i.id === user.onboardingInvoiceId) &&
      (i.category === 'Yuran Pendaftaran' || (i.title && i.title.toLowerCase().includes('pendaftaran ahli baharu')))
    );
    if (onbInv && onbInv.status === 'Lulus / Dibayar') {
      return false;
    }
  }

  // User is a new Ahli who has not completed onboarding payment
  return user.role === 'Ahli' || !user.role;
}


